import { useMemo } from 'react'
import {
  PanelRoot,
  ResizableMainArea,
  SideBarArea,
  SingleSheetRoot,
} from '../../../../containers/SingleSheetV2'
import {
  TabMainArea,
  TabPanelArea,
} from '../../../../containers/SingleSheetV2/Tab'
import { PageArea } from '../../..'
import { WbsItemType } from '../../../../../domain/entity/WbsItemEntity'
import { TicketProps } from '../..'
import { MultilineTextEditorTab } from '../tab-panel/multiline-text'
import { ProjectPlanTab } from '../tab-panel/project-plan'
import { AttachmentTab } from '../tab-panel/attachment'
import { DiscussionTab } from '../tab-panel/discussion'
import { TaskActualWorkTabPanel } from '../tab-panel/TaskActualWorkTabPanel'
import { WbsItemHeader } from '../header'
import { TaskSubHeader } from '../subheader'
import { DevelopmentTabPanel } from '../tab-panel/DevelopmentTabPanel'
import { ChangeLogTabPanel } from '../tab-panel/change-log'
import { useWbsItemSelectOptions } from '../../hooks/wbsItemSelectOptions'
import Loading from '../../../../components/process-state-notifications/Loading'
import { useAttachmentHandlers } from '../../hooks/attachments'
import { useWbsItemPropertySearch } from '../../hooks/wbsItemPropertySearch'
import { useWbsItemModelModifier } from '../../hooks/wbsItemModelModifier'
import { useTicketListSearch } from '../../hooks/tickets/ticketListSearch'
import { SingleSheetWrapper } from '.'
import { useTicketSingleSheetData } from '../../hooks/tickets/data'
import { useWbsItemAdditionalPropertiesBySingleType } from '../../../../hooks/useWbsItemAdditionalProperties'
import { useSingleSheetKeyBind } from '../../../../containers/SingleSheetV2/hooks/keyBind'
import CancelConfirmDialog, {
  useCancelConfirmDialog,
} from '../../../../components/dialogs/CancelConfirmDialog'
import TaskActualWorkRegistrationDialog, {
  useTaskActualWorkRegistrationDialog,
} from '../dialog/TaskActualWorkRegistrationDialog'
import { useResizableSingleSheet } from '../../../../containers/SingleSheetV2/hooks/resizableArea'
import {
  useWbsItemLeftTabs,
  WbsItemLeftTab,
  WbsItemLeftTabs,
} from '../tab/LeftTabs'
import {
  TaskRightTabs,
  useWbsItemRightTabs,
  WbsItemRightTab,
} from '../tab/RightTabs'
import { useBrowserTabNameSetter } from '../../hooks/browserTabNameSetter'
import { useSingleSheetFormValidation } from '../../../../containers/SingleSheetV2/hooks/formValidation'
import { useTicketBasicInformationTableDef } from './basic-information-table-def/ticket'
import { BasicInformationTable } from '../../../../containers/SingleSheetV2/table'
import { useFunctionPropertyConfiguration } from '../../hooks/functionPropertyConfiguration'
import ConfirmDialog, { useConfirmDialog } from '../dialog/ConfirmDialog'
import { useTicketSubmitButton } from '../../hooks/tickets/ticketSubmitButton'
import { useTicketChangeLogParser } from '../../hooks/tickets/ticketChangeLogParser'
import { useTicketLabel } from '../../hooks/tickets/label'
import { useMultiLineTexTabDefsOnlyDescription } from './tab-def'

export const Ticket = ({
  projectUuid,
  wbsItemType: _wbsItemType,
  uuid,
  functionUuid,
  ticketType: _ticketType,
  functionLayerDepth,
  ticketInitialValue,
}: TicketProps) => {
  const singleSheetRef = useResizableSingleSheet<HTMLDivElement>()
  const { openConfirmDialog, confirmDialogProps } = useConfirmDialog()
  const {
    initialized,
    isCreatingWbsItem,
    edited,
    editedFormData,
    isLoading,
    wbsItemFormModel,
    wbsItemType,
    ticketFormModel,
    ticketType,
    ticketTypeIsChanged,
    onChangeWbsItem,
    onChangeTicketList,
    ticketModelModifiers,
    onChangeAdditionalPropertyValue: _onChangeAdditionalPropertyValue,
    reload,
    submit,
  } = useTicketSingleSheetData(
    uuid,
    _wbsItemType,
    _ticketType,
    ticketInitialValue
  )

  useBrowserTabNameSetter(
    wbsItemFormModel.displayName || '',
    functionLayerDepth,
    initialized
  )

  const { leftTab, onChangeLeftTab } = useWbsItemLeftTabs(isCreatingWbsItem)
  const { rightTab, onChangeRightTab } = useWbsItemRightTabs(isCreatingWbsItem)
  const {
    subStatuses,
    difficulties,
    priorities,
    getSubStatusByStatus,
    getStatusBySubStatus,
  } = useWbsItemSelectOptions(projectUuid, WbsItemType.TASK, ticketType)
  const { searchTicketList } = useTicketListSearch(projectUuid)
  const { searchTeam, searchMember, searchSprint } = useWbsItemPropertySearch(
    projectUuid,
    wbsItemFormModel
  )
  const { wbsItemAdditionalProperties } =
    useWbsItemAdditionalPropertiesBySingleType(projectUuid, wbsItemType)
  const propertyConfiguration = useFunctionPropertyConfiguration(
    projectUuid,
    wbsItemType,
    ticketType
  )

  const basicInformationTableDef = useTicketBasicInformationTableDef(
    projectUuid,
    {
      subStatuses,
      difficulties,
      priorities,
    },
    {
      searchTeam,
      searchMember,
      searchSprint,
    },
    searchTicketList,
    wbsItemAdditionalProperties,
    propertyConfiguration
  )

  const multilineTextTabDefs = useMultiLineTexTabDefsOnlyDescription()
  const {
    validate,
    validateAllProperties,
    validationErrors,
    hasValidationErrors,
  } = useSingleSheetFormValidation(
    basicInformationTableDef,
    multilineTextTabDefs,
    edited
  )

  const getChangeLogParser = useTicketChangeLogParser(
    {
      subStatuses,
      difficulties,
      priorities,
    },
    wbsItemAdditionalProperties
  )

  const {
    onChangeDisplayName,
    onChangeStatus,
    onChangeSubStatus,
    onChangeTags,
    onChangeTeam,
    onChangeAccountable,
    onChangeResponsible,
    onChangeAssignee,
    onChangeWatchers,
    onChangeDifficulty,
    onChangePriority,
    onChangeEstimatedHour,
    onChangeSprint,
    onChangeScheduledDate,
    onChangeActualDate,
    onChangeDescription,
    formModelModifiers: wbsItemModelModifiers,
  } = useWbsItemModelModifier(
    onChangeWbsItem,
    _onChangeAdditionalPropertyValue,
    {
      getStatusBySubStatus,
      getSubStatusByStatus,
    },
    wbsItemAdditionalProperties,
    validate
  )

  const { deliverableAttachments, addAttachments, deleteAttachments } =
    useAttachmentHandlers(
      uuid,
      _wbsItemType,
      wbsItemFormModel.deliverableAttachments
    )
  const labelMap = useTicketLabel(
    wbsItemAdditionalProperties,
    propertyConfiguration
  )
  const {
    statusLabel,
    subStatusLabel,
    priorityLabel,
    teamLabel,
    accountableLabel,
    responsibleLabel,
    assigneeLabel,
    scheduledDateLabel,
    actualDateLabel,
    estimatedHourLabel,
    actualHourLabel,
  } = useMemo(() => {
    return {
      statusLabel: labelMap.get('status') || '',
      subStatusLabel: labelMap.get('substatus') || '',
      priorityLabel: labelMap.get('priority') || '',
      teamLabel: labelMap.get('team') || '',
      accountableLabel: labelMap.get('accountable') || '',
      responsibleLabel: labelMap.get('responsible') || '',
      assigneeLabel: labelMap.get('assignee') || '',
      scheduledDateLabel: labelMap.get('scheduledDate') || '',
      actualDateLabel: labelMap.get('actualDate') || '',
      estimatedHourLabel: labelMap.get('estimatedHour') || '',
      actualHourLabel: labelMap.get('actualHour') || '',
    }
  }, [labelMap, labelMap.size])

  const { openDialog: openCancelConfirmDialog, ...cancelConfirmDialogProps } =
    useCancelConfirmDialog(reload)
  const {
    openDialog: openTaskActualWorkRegistrationDialog,
    ...taskActualWorkRegistrationDialogProps
  } = useTaskActualWorkRegistrationDialog()

  const formModel = useMemo(
    () => ({
      ...wbsItemFormModel,
      ...ticketFormModel,
    }),
    [wbsItemFormModel, ticketFormModel]
  )
  const formModelModifiers = useMemo(
    () => ({
      ...wbsItemModelModifiers,
      ...ticketModelModifiers,
    }),
    [wbsItemModelModifiers, ticketModelModifiers]
  )

  const { onClickSubmitButton, submitButtonIsActive } = useTicketSubmitButton(
    ticketTypeIsChanged,
    openConfirmDialog,
    submit,
    edited,
    formModel,
    hasValidationErrors,
    validateAllProperties
  )

  useSingleSheetKeyBind(onClickSubmitButton, functionUuid)

  return (
    <PageArea>
      <Loading isLoading={isLoading} />
      {initialized && (
        <>
          <WbsItemHeader
            uuid={uuid}
            isCreatingWbsItem={isCreatingWbsItem}
            title={wbsItemFormModel.displayName || ''}
            onChangeTitle={onChangeDisplayName}
            type={wbsItemType}
            watchers={wbsItemFormModel.watchers}
            onChangeWatchers={onChangeWatchers}
            functionLayerDepth={functionLayerDepth}
          />
          <TaskSubHeader
            projectUuid={projectUuid}
            submit={onClickSubmitButton}
            reload={edited ? openCancelConfirmDialog : reload}
            submitIsActive={submitButtonIsActive}
            model={wbsItemFormModel}
            statusLabel={statusLabel}
            subStatusLabel={subStatusLabel}
            priorityLabel={priorityLabel}
            teamLabel={teamLabel}
            accountableLabel={accountableLabel}
            responsibleLabel={responsibleLabel}
            assigneeLabel={assigneeLabel}
            scheduledDateLabel={scheduledDateLabel}
            actualDateLabel={actualDateLabel}
            estimatedHourLabel={estimatedHourLabel}
            actualHourLabel={actualHourLabel}
            onChangeStatus={onChangeStatus}
            onChangeSubStatus={onChangeSubStatus}
            onChangePriority={onChangePriority}
            onChangeTeam={onChangeTeam}
            onChangeAccountable={onChangeAccountable}
            onChangeResponsible={onChangeResponsible}
            onChangeAssignee={onChangeAssignee}
            onChangeScheduledDate={onChangeScheduledDate}
            onChangeActualDate={onChangeActualDate}
            onChangeEstimatedHour={onChangeEstimatedHour}
            subStatuses={subStatuses}
            priorities={priorities}
            isCreatingWbsItem={isCreatingWbsItem}
            onClickTaskActualWorkRegistrationButton={
              openTaskActualWorkRegistrationDialog
            }
          />
          <SingleSheetWrapper>
            <SingleSheetRoot ref={singleSheetRef}>
              <ResizableMainArea singleSheetRootRef={singleSheetRef}>
                <TabPanelArea>
                  <WbsItemLeftTabs
                    isCreatingWbsItem={isCreatingWbsItem}
                    leftTab={leftTab}
                    onChangeLeftTab={onChangeLeftTab}
                  />
                  <TabMainArea>
                    <PanelRoot>
                      {leftTab === WbsItemLeftTab.BASIC_INFORMATION && (
                        <BasicInformationTable
                          dataUuid={uuid}
                          tableDef={basicInformationTableDef}
                          formModel={formModel}
                          formModelModifiers={formModelModifiers}
                          editedFormData={editedFormData}
                          validationErrors={validationErrors}
                        />
                      )}
                      {leftTab === WbsItemLeftTab.DESCRIPTION && (
                        <MultilineTextEditorTab
                          uuid={uuid}
                          externalId="task.description"
                          value={wbsItemFormModel.description}
                          onChange={onChangeDescription}
                        />
                      )}
                      {leftTab === WbsItemLeftTab.SUB_TASK && (
                        <ProjectPlanTab uuid={uuid} />
                      )}
                      {leftTab === WbsItemLeftTab.ATTACHMENT && (
                        <AttachmentTab
                          attachments={deliverableAttachments}
                          acceptingFileTypes={[]}
                          onUpload={addAttachments}
                          onDelete={deleteAttachments}
                        />
                      )}
                    </PanelRoot>
                  </TabMainArea>
                </TabPanelArea>
              </ResizableMainArea>
              <SideBarArea>
                <TabPanelArea>
                  <TaskRightTabs
                    isCreatingWbsItem={isCreatingWbsItem}
                    rightTab={rightTab}
                    onChangeRightTab={onChangeRightTab}
                  />
                  <TabMainArea>
                    <PanelRoot>
                      {rightTab === WbsItemRightTab.DISCUSSION && (
                        <DiscussionTab
                          functionUuid={functionUuid}
                          projectUuid={projectUuid}
                          uuid={uuid}
                          wbsItemType={wbsItemType}
                        />
                      )}
                      {rightTab === WbsItemRightTab.ACTUAL_WORK && (
                        <TaskActualWorkTabPanel uuid={uuid} />
                      )}
                      {rightTab === WbsItemRightTab.CHANGE_LOG && (
                        <ChangeLogTabPanel
                          uuids={[uuid, ticketFormModel.uuid]}
                          getParser={getChangeLogParser}
                          labelMap={labelMap}
                        />
                      )}
                      {rightTab === WbsItemRightTab.DEVELOPMENT && (
                        <DevelopmentTabPanel uuid={uuid} />
                      )}
                      {/** Show the description tab only during creating wbs item on SingleSheet. */}
                      {rightTab === WbsItemRightTab.DESCRIPTION && (
                        <MultilineTextEditorTab
                          uuid={uuid}
                          externalId="task.description"
                          value={wbsItemFormModel.description}
                          onChange={onChangeDescription}
                        />
                      )}
                    </PanelRoot>
                  </TabMainArea>
                </TabPanelArea>
              </SideBarArea>
            </SingleSheetRoot>
          </SingleSheetWrapper>
          {taskActualWorkRegistrationDialogProps.open && (
            <TaskActualWorkRegistrationDialog
              taskUuid={wbsItemFormModel.uuid}
              projectUuid={projectUuid}
              wbsItemType={_wbsItemType}
              {...taskActualWorkRegistrationDialogProps}
            />
          )}
          <CancelConfirmDialog {...cancelConfirmDialogProps} />
          <ConfirmDialog {...confirmDialogProps} />
        </>
      )}
    </PageArea>
  )
}
